import { attr, belongsTo, hasMany } from '@warp-drive/legacy/model';

import Model from 'umg-ember-backend/models/variant-model';
import {
  saveHasManyRelationships,
  updateUrlAliasParents,
} from 'umg-ember-backend/utils/model-save-hooks';
import {
  buildValidations,
  disabledWhenVariant,
  validator,
} from 'umg-ember-backend/utils/validations';

const Validations = buildValidations({
  mmcIdSeries: validator('number'),
  name: validator('presence', {
    presence: true,
    disabled: disabledWhenVariant(),
  }),
  screenname: validator('presence', {
    presence: true,
    disabled: disabledWhenVariant(),
  }),
  themeType: validator('presence', {
    presence: true,
    disabled: disabledWhenVariant(),
  }),
});

export const settings = {
  copyableOptions: {
    ignoreAttributes: ['cmsContentModified'],
    relationsToCopy: ['externalReferences'],
  },
  saveHooks: {
    after: [
      updateUrlAliasParents,
      saveHasManyRelationships(
        'urlAliases',
        'externalReferences',
        'artistFunctions',
      ),
    ],
  },
  variantProperties: [
    'cmsLanguageId',
    'seoTitle',
    'seoDescription',
    'keywords',
    'seoImage',
    'cmsState',
    'teaserHeadline',
    'teaserText',
    'text',
  ],
};

export default class ArtistModel extends Model.extend(Validations) {
  @attr('string') colorArtist;
  @attr('string') connectLocalArtistId;
  @attr('string') connectLocalName;
  @attr dateBirth;
  @attr dateDeath;
  @attr({
    defaultValue: function () {
      return {
        items: [],
      };
    },
  })
  mmcIds;
  @attr('number', { defaultValue: 0 }) mmcIdSeries;
  @attr('string') name;
  @attr({
    defaultValue: function () {
      return {
        items: [],
      };
    },
  })
  rmsIds;
  @attr('string') screenname;
  @attr('boolean') showHome;
  @attr('boolean', { defaultValue: true }) showNwlt;
  @attr('boolean') showSpotifyPlaylistGenerator;
  @attr('string') text;
  @attr('string') themeType;
  @attr('boolean') visibleInList;

  // CMS fields
  @attr('string') seoTitle;
  @attr('string') seoDescription;
  @attr('string') keywords;
  @attr('number', { defaultValue: 2 }) cmsState;

  // Meta fields
  @attr cmsCreated;
  @attr cmsDeleted;
  @attr cmsLocktime;
  @attr cmsModified;
  @attr cmsContentModified;
  @attr() cmsHistory;
  @belongsTo('user', { async: true, inverse: null }) cmsOwnerId;
  @belongsTo('user', { async: true, inverse: null }) cmsCreatorId;
  @belongsTo('user', { async: true, inverse: null }) cmsLockerId;
  @belongsTo('user', { async: true, inverse: null }) cmsModifierId;

  // Relations
  @hasMany('label', { async: true, inverse: null }) alternativeLabels;
  @hasMany('artist-function-relation', { async: true, inverse: 'artist' })
  artistFunctions;
  @belongsTo('asset', { async: true, inverse: null }) assetId;
  @belongsTo('asset', { async: true, inverse: null }) assetIdEyecatcher;
  @belongsTo('asset', { async: true, inverse: null }) assetIdImage;
  @belongsTo('asset', { async: true, inverse: null }) seoImage;
  @belongsTo('asset', { async: true, inverse: null }) promotionImage;
  @hasMany('category', { async: true, inverse: null }) categories;
  @hasMany('channel', { async: true, inverse: null }) channels;
  @hasMany('collection', { async: true, inverse: null }) collections;
  @hasMany('person', { async: true, inverse: null }) contactPeople;
  @hasMany('division', { async: true, inverse: null }) divisions;
  @hasMany('external-reference', { async: true, inverse: 'artist' })
  externalReferences;
  @belongsTo('channel', { async: true, inverse: null }) firstChannel;
  @hasMany('label', { async: true, inverse: null }) labels;
  @hasMany('newsletter-config', { async: true, inverse: null })
  newsletterConfigs;
  @belongsTo('channel', { async: true, inverse: null }) ownedByChannel;
  @hasMany('product', { async: true, inverse: null }) products;
  @hasMany('artist', { async: true, inverse: null }) similarArtists;
  @hasMany('story', { async: true, inverse: null }) stories;
  @hasMany('taxon', { async: true, inverse: null }) taxa;
  @hasMany('url-alias', { async: true, inverse: null }) urlAliases;

  // Getter, setter and computed properties
  get displayHeadline() {
    return this.screenname;
  }

  get headline() {
    return this.screenname;
  }

  get connectArtistInfo() {
    let id = this.connectLocalArtistId;
    let name = this.connectLocalName;

    if (!id) {
      return '';
    }

    return `${name} (${id})`;
  }

  get displayLabel() {
    return `${this.name} (${this.id})`;
  }
}
